import type { ConvexClient } from "convex/browser";
import { makeFunctionReference } from "convex/server";

import { readWebHostSession } from "./webHostSession";
import { readWorldConvexClient } from "./worldConvexClient";
import { forgetWindowGrant, windowGrantBearer, WINDOW_GRANT_RENEW_WITHIN_MS } from "./worldMcp";

/**
 * cleo-web (steps 5-7 design §12c, 7.4.4a): the person's notification feed in a window with no Mac behind it.
 *
 * The Desk's server watches the World's `worldNotifications:watch` with its deposited grant and streams each answer to its
 * window as one line (apps/server notificationChanges.ts); the window's observer reads that stream (notificationObserver.ts).
 * A browser host has no such server, so it watches the same query itself, on its own connection to the World, with its own
 * window grant (worldMcp.ts windowGrantBearer), and gives the observer the same lines: `changed` with the World's value, or
 * `refused` for a World whose grant the World will not give this person. The grant is renewed while the watch is open,
 * as a call would renew it; an answer of no value (the grant ended) forgets it and ends the stream, and the observer
 * reconnects, minting again.
 */

type Snapshot = { actorId: string } & Record<string, unknown>;
const watchRef = makeFunctionReference<
  "query",
  { bearer: string; worldKey: string },
  Snapshot | null
>("worldNotifications:watch");

export type WindowWatchDeps = {
  readonly client: ConvexClient | null;
  readonly owner: string | null;
  readonly bearer: typeof windowGrantBearer;
  readonly forget: typeof forgetWindowGrant;
  readonly now: () => number;
  readonly setTimer: (fn: () => void, ms: number) => unknown;
  readonly clearTimer: (timer: unknown) => void;
};

function liveDeps(): WindowWatchDeps {
  const session = readWebHostSession();
  return {
    client: readWorldConvexClient(),
    owner: session.state === "accepted" ? session.actorId : null,
    bearer: windowGrantBearer,
    forget: forgetWindowGrant,
    now: Date.now,
    setTimer: (fn, ms) => setTimeout(fn, ms),
    clearTimer: (timer) => clearTimeout(timer as ReturnType<typeof setTimeout>),
  };
}

/** The World's own words for a World this person may not hold a grant to (backend windowGrants.js). */
const NOT_A_MEMBER = /absent or inactive|not an active member/u;

/** The shortest wait between renewals, so a grant the World keeps short never turns this into a loop. */
const MIN_RENEW_WAIT_MS = 10_000;

/** The window's notification lines for these Worlds, in the shape the Desk's watch route streams. */
export async function watchWindowNotifications(
  input: { readonly worldKeys: readonly string[] },
  signal: AbortSignal,
  deps: WindowWatchDeps = liveDeps(),
): Promise<Response> {
  signal.throwIfAborted();
  const { client, owner } = deps;
  if (client === null || owner === null)
    throw new Error("This window is not signed in to the World.");
  const stops: Array<() => void> = [];
  let closed = false;
  let abortAll = () => {};
  const body = new ReadableStream<Uint8Array>({
    start(controller) {
      const send = (event: object) => {
        if (!closed) controller.enqueue(new TextEncoder().encode(`${JSON.stringify(event)}\n`));
      };
      const end = (error?: Error) => {
        if (closed) return;
        closed = true;
        signal.removeEventListener("abort", abort);
        for (const stop of stops.splice(0)) stop();
        if (error) controller.error(error);
        else controller.close();
      };
      const abort = () => end();
      abortAll = abort;
      signal.addEventListener("abort", abort);
      for (const worldKey of input.worldKeys) {
        let unsubscribe: (() => void) | null = null;
        let renewal: unknown = null;
        let deadline: unknown = null;
        let token: string | null = null;
        let grantId: string | null = null;
        let expiresAt = 0;
        const stopWorld = () => {
          if (renewal !== null) deps.clearTimer(renewal);
          if (deadline !== null) deps.clearTimer(deadline);
          renewal = deadline = null;
          unsubscribe?.();
          unsubscribe = null;
        };
        // The grant's own end, kept apart from its renewal (as the Desk's worldSubscriptions.ts does): a renewal that stalls
        // or fails never leaves the watch showing what the World said under a grant that has ended.
        const lapsed = () => {
          if (grantId !== null) deps.forget(worldKey, grantId);
          end(new Error("Notifications are reconnecting."));
        };
        const extend = (until: number) => {
          if (until <= expiresAt) return;
          expiresAt = until;
          if (deadline !== null) deps.clearTimer(deadline);
          deadline = deps.setTimer(lapsed, Math.max(0, until - deps.now()));
        };
        const subscribe = (grant: { grantId: string; token: string }) => {
          unsubscribe?.();
          token = grant.token;
          unsubscribe = client.onUpdate(
            watchRef,
            { bearer: grant.token, worldKey },
            (value) => {
              if (closed) return;
              if (
                value &&
                typeof value.actorId === "string" &&
                typeof value["expiresAt"] === "number" &&
                value["expiresAt"] > deps.now()
              ) {
                extend(value["expiresAt"]);
                send({ kind: "changed", worldKey, value });
                return;
              }
              // The World no longer answers to this grant (it lapsed, or its Clerk session ended): mint again on reconnect.
              lapsed();
            },
            (error) => end(error instanceof Error ? error : new Error(String(error))),
          );
        };
        const hold = async () => {
          const grant = await deps.bearer({ client, owner, worldKey });
          if (closed) return;
          if (!grant.ok) {
            // A World that says this person is not its active member is refused, as the Desk's watch says, and its watch
            // stops; anything else (a lost sign-in, a dropped connection) ends the stream, and the observer tries again.
            if (grant.kind === "upstream_refusal" && NOT_A_MEMBER.test(grant.reason)) {
              stopWorld();
              send({ kind: "refused", worldKey });
            } else end(new Error(grant.reason));
            return;
          }
          grantId = grant.grantId;
          if (grant.token !== token) {
            expiresAt = 0;
            subscribe(grant);
          }
          extend(grant.expiresAt);
          const wait = Math.max(
            MIN_RENEW_WAIT_MS,
            grant.expiresAt - WINDOW_GRANT_RENEW_WITHIN_MS - deps.now() + 1_000,
          );
          renewal = deps.setTimer(() => void hold().catch((error) => end(error as Error)), wait);
        };
        stops.push(stopWorld);
        void hold().catch((error) => end(error as Error));
      }
    },
    cancel() {
      closed = true;
      signal.removeEventListener("abort", abortAll);
      for (const stop of stops.splice(0)) stop();
    },
  });
  return new Response(body, { status: 200, headers: { "content-type": "application/x-ndjson" } });
}
